import React from 'react';
import { cn } from '@/lib/utils';
import { STRENGTH_META, OPPORTUNITY_TYPE_META, OPPORTUNITY_TYPE_LABEL, OPPORTUNITY_STATUS_META, strengthFromConfidence, CLASSIFICATION_META, DECISION_META, PRICING_META, fmtAmount } from '@/lib/format';

const base = 'inline-flex items-center gap-1.5 rounded-full px-2.5 py-0.5 text-[11px] font-semibold';

export const ClassificationBadge = ({ classification, testid }) => {
  const meta = CLASSIFICATION_META[classification] || CLASSIFICATION_META.needs_review;
  return (
    <span className={cn(base, meta.cls)} data-testid={testid}>
      <span className={cn('h-1.5 w-1.5 rounded-full', meta.dot)} />{meta.label}
    </span>
  );
};

export const PricingBadge = ({ status, testid }) => {
  const meta = PRICING_META[status] || PRICING_META.unquantified;
  return (
    <span className={cn(base, meta.cls)} data-testid={testid}>
      <span className={cn('h-1.5 w-1.5 rounded-full', meta.dot)} />{meta.label}
    </span>
  );
};

export const DecisionBadge = ({ decision, testid }) => {
  if (!decision) return null;
  const meta = DECISION_META[decision];
  if (!meta) return null;
  return <span className={cn(base, meta.cls)} data-testid={testid}>{meta.label}</span>;
};

export const StrengthBadge = ({ strength, confidence, testid }) => {
  const key = strength || strengthFromConfidence(confidence);
  const meta = STRENGTH_META[key] || STRENGTH_META.worth_checking;
  return (
    <span className={cn(base, meta.cls)} data-testid={testid}>
      <span className={cn('h-1.5 w-1.5 rounded-full', meta.dot)} />
      {meta.label}
    </span>
  );
};

export const TypeBadge = ({ type, testid }) => {
  const cls = OPPORTUNITY_TYPE_META[type] || OPPORTUNITY_TYPE_META.variation;
  return <span className={cn(base, cls)} data-testid={testid}>{OPPORTUNITY_TYPE_LABEL[type] || 'Variation'}</span>;
};

export const OppStatusBadge = ({ status, testid }) => {
  const meta = OPPORTUNITY_STATUS_META[status] || OPPORTUNITY_STATUS_META.new;
  return <span className={cn(base, meta.cls)} data-testid={testid}>{meta.label}</span>;
};

/**
 * Deterministic amount-or-status display. NEVER shows AI-guessed money.
 * - Fully priced: shows the deterministic final amount (in the calc currency, no FX).
 * - Otherwise: shows the pricing status only (Unquantified / Partially priced).
 * Use `compact` for tight cells (board cards / table rows).
 */
export const AmountOrStatus = ({ opportunity, compact = false, align = 'right', testid }) => {
  const calc = (opportunity && opportunity.calculation) || {};
  const status = (opportunity && opportunity.pricing_status) || calc.pricing_status || 'unquantified';
  if (status === 'fully_priced' && calc.final_amount != null) {
    return (
      <span
        className={cn('font-mono font-semibold text-money-700', compact ? 'text-sm' : 'text-sm')}
        data-testid={testid}
      >
        {fmtAmount(calc.final_amount, calc.currency || 'USD')}
      </span>
    );
  }
  const meta = PRICING_META[status] || PRICING_META.unquantified;
  return (
    <span
      className={cn('inline-flex items-center gap-1.5 rounded-full px-2 py-0.5 text-[11px] font-semibold', meta.cls)}
      data-testid={testid}
    >
      <span className={cn('h-1.5 w-1.5 rounded-full', meta.dot)} />
      {meta.label}
    </span>
  );
};
